import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams } from 'react-router-dom';
import uniqid from 'uniqid';
import _ from 'lodash';
import Case from 'case';
import {
  Box, Text, AccordionPanel,
} from 'grommet';
import { FormNext } from 'grommet-icons';
import {
  authLogOutRequest,
  authUploadCompanyLogoRequest,
  initiateCompanySettingsRequest,
  startTourRequest,
  updateUserPreferenceRequest,
} from '@Actions';
import { SystemStatusNotice, ThemeToggleButton } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { paths } from '@Components/configs';
import companyFeatureFlags from '@Helpers/featureFlags';
import { localAsset } from '@Helpers/asset';
import { pluralize } from '@Helpers/util';
import {
  NavbarSmallLayout,
  NavbarAuthLayout,
  MenuDrop,
  ProfileSection,
  MobileMenuButton,
  MenuAnchor,
} from './HeaderComponent';
import {
  StyledAnchorButton, StyledProfileDropDownAnchor, StyledAccordion,
} from './StyledHeader';
import { StyledSVG } from '../Sidenav/StyledAuthNavWrapper';
import style from './Header.module.scss';

const AuthHeader = ({
  small,
  isAuthenticated,
  location,
  logOut,
  uploadCompanyLogo,
  updateUserPreference,
  fetchCompanySettings,
  startTour,
  onboardingDataLoading,
  companySettings = null,
  companyMaintenanceSettings = null,
  onboardingData = null,
  auth = null,
  customReportsConfig = null,
}) => {
  const [showOverlay, setShowOverlay] = React.useState(false);
  const [isScroll, setIsScroll] = React.useState(false);
  const [collapseOverride, setCollapseOverride] = React.useState(false);
  const authNavbarRef = React.useRef(null);
  const companyLogoInputRef = React.useRef(null);
  const { id } = useParams();
  const {
    theme: { page, navigation, button },
    accountPlaceholder,
    hideBlacklightSearchCompanies,
    isDarkMode,
    setDarkMode,
  } = useAppTheme();

  React.useEffect(() => {
    if (!companySettings) fetchCompanySettings();

    window.addEventListener('scroll', () => {
      if (showOverlay || collapseOverride || small) return;
      const navbar = authNavbarRef.current;
      if (!navbar) return;
      const scrollThreshold = Math.ceil(navbar.getBoundingClientRect().top + 25);
      setIsScroll(window.scrollY >= scrollThreshold);
    });

    if (location.pathname === '/piracy-overview' || (id && location.pathname === `/reports/${id}`)) {
      setCollapseOverride(true);
    } else {
      setCollapseOverride(false);
    }

    return () => {
      window.removeEventListener('scroll', () => {});
    };
  }, [location.pathname]);

  const isRedflagUploadCurrent = onboardingData?.youtubeCid?.uploader === 'redflag';
  const stableIsRedflagUploadRef = React.useRef(isRedflagUploadCurrent);
  if (!onboardingDataLoading) {
    stableIsRedflagUploadRef.current = isRedflagUploadCurrent;
  }
  const isRedflagUpload = stableIsRedflagUploadRef.current;

  if (!isAuthenticated || !auth) {
    return null;
  }

  const {
    email,
    firstName,
    lastName,
    companyId,
    companyName,
    companyLogo,
    companyFeatures,
    themePreference,
  } = auth;

  const {
    overviewEnabled,
    searchEnabled,
    searchConfig,
    settingsEnabled,
    liveTrackerEnabled,
    reportsEnabled,
    contentIdEnabled,
    contentIdAssetUploadEnabled,
    cyclopsEnabled,
    cyclopsAnalyticsDisabled,
    cyclopsSettingsDisabled,
    isCyclopsDemo,
  } = companyFeatureFlags(companyFeatures) || {};

  const useFeatures = companyFeatures && _.filter(companyFeatures, (({ key }) => key === 'export' || key === 'review'));
  const hideSearchFeatures = hideBlacklightSearchCompanies.includes(companyId) || !searchEnabled;
  const displayStatusNotice = Boolean(
    companyMaintenanceSettings?.showStatusMessage,
  );
  const statusMessage = companyMaintenanceSettings?.statusMessage;
  const teamsSettingsEnabled = Boolean(searchConfig?.enabledSearchSubtypes && searchConfig.enabledSearchSubtypes.includes('Sports'));
  const unitTerm = searchConfig?.unitTerm;
  const unitsSettingsEnabled = Boolean(unitTerm);
  const showSettings = Boolean(settingsEnabled && companySettings?.length > 0);
  const showAltSettings = Boolean(teamsSettingsEnabled || unitsSettingsEnabled);
  const showThemeTooltip = themePreference === undefined || themePreference === null;
  const customReports = customReportsConfig && customReportsConfig.find(({ metadata }) => (
    metadata?.config?.companyId === companyId && metadata?.config?.companyName === companyName));
  const withCustomReports = customReports?.metadata?.config?.customReports?.length > 0;
  const withAnalytics = overviewEnabled || reportsEnabled
    || withCustomReports || liveTrackerEnabled;

  const selectFeatureRedirect = (key) => {
    switch (key) {
      case 'review':
        return paths.productReview;
      case 'summary':
        return null;
      default:
        return null;
    }
  };

  if (small) {
    return (
      <NavbarSmallLayout
        darkBackground={isDarkMode}
        navbarRef={authNavbarRef}
        toggle={() => setShowOverlay(!showOverlay)}
        showOverlay={showOverlay}
        navBg={navigation.background}
        navBorder={navigation.border}
        primaryTextColor={page.primaryText}
        secondaryMenuContent={(
          <MobileMenuButton
            label="Sign Out"
            path={paths.login}
            onClick={() => {
              logOut();
              setShowOverlay(!showOverlay);
            }}
            buttonColor={button.primaryBg}
            icon={(
              <Box flex={false} width="20px" height="20px" margin={{ right: '12px' }}>
                <StyledSVG
                  src={localAsset('images/sign-in-icon.svg')}
                  height="20px"
                  width="20px"
                  $fillColor="white"
                />
              </Box>
            )}
          />
        )}
      >
        <Box flex direction="column" gap="1.5rem" pad={{ top: '1.5rem' }}>
          <StyledAccordion
            iconColor="var(--button-icon-color)"
            highlightColor="var(--button-icon-hover-color)"
          >
            {withAnalytics && (
              <AccordionPanel label={<MenuAnchor label="Analytics" color={page.primaryText} />}>
                <Box align="stretch" pad={{ horizontal: '1rem' }}>
                  {overviewEnabled && (
                    <MenuAnchor
                      path={paths.productPiracyOverview}
                      label="Piracy overview"
                      onClick={() => setShowOverlay(!showOverlay)}
                      color={page.primaryText}
                      className={style.accordionItem}
                    />
                  )}
                  {(reportsEnabled || withCustomReports) && (
                    <MenuAnchor
                      path={paths.productReports}
                      label="All reports"
                      onClick={() => setShowOverlay(!showOverlay)}
                      color={page.primaryText}
                      className={style.accordionItem}
                    />
                  )}
                  {liveTrackerEnabled && (
                    <MenuAnchor
                      path={paths.productLiveTracker}
                      label="Live tracker"
                      onClick={() => setShowOverlay(!showOverlay)}
                      color={page.primaryText}
                      className={style.accordionItem}
                    />
                  )}
                </Box>
              </AccordionPanel>
            )}
            {!hideSearchFeatures && (
              <AccordionPanel label={<MenuAnchor label="Results" color={page.primaryText} />}>
                <Box align="stretch" pad={{ horizontal: '1rem' }}>
                  <MenuAnchor
                    path={paths.productInfringements}
                    label="All infringements"
                    onClick={() => setShowOverlay(!showOverlay)}
                    color={page.primaryText}
                    className={style.accordionItem}
                  />
                  <MenuAnchor
                    path={paths.productConfirmedRemovals}
                    label="Confirmed removals"
                    onClick={() => setShowOverlay(!showOverlay)}
                    color={page.primaryText}
                    className={style.accordionItem}
                  />
                  <MenuAnchor
                    path={paths.productListSearches.replace(':status', 'all')}
                    label="All searches"
                    onClick={() => setShowOverlay(!showOverlay)}
                    color={page.primaryText}
                    className={style.accordionItem}
                  />
                </Box>
              </AccordionPanel>
            )}
            {contentIdEnabled && (
              <AccordionPanel label={<MenuAnchor label="Content ID" color={page.primaryText} />}>
                <Box align="stretch" pad={{ horizontal: '1rem' }}>
                  {contentIdAssetUploadEnabled && (
                    <MenuAnchor
                      path={paths.productAssetUpload}
                      label="Upload assets"
                      onClick={() => setShowOverlay(!showOverlay)}
                      color={page.primaryText}
                      className={style.accordionItem}
                    />
                  )}
                  <MenuAnchor
                    path={paths.productListAssets}
                    label="Manage assets"
                    onClick={() => setShowOverlay(!showOverlay)}
                    color={page.primaryText}
                    className={style.accordionItem}
                  />
                  <MenuAnchor
                    path={paths.productAllClaimsList}
                    pathMatch={(p) => p === paths.productAllClaimsList
                      || p.startsWith('/content-id/claims/')
                      || p.startsWith('/content-id/manual-claims')}
                    label="Manage claims"
                    onClick={() => setShowOverlay(!showOverlay)}
                    color={page.primaryText}
                    className={style.accordionItem}
                  />
                  <MenuAnchor
                    path={paths.productCidAllowlist}
                    label="Channel allowlist"
                    onClick={() => setShowOverlay(!showOverlay)}
                    color={page.primaryText}
                    className={style.accordionItem}
                  />
                </Box>
              </AccordionPanel>
            )}
            {(showSettings || showAltSettings) && (
              <AccordionPanel label={<MenuAnchor label="Settings" color={page.primaryText} />}>
                <Box align="stretch" pad={{ horizontal: '1rem' }}>
                  {teamsSettingsEnabled && (
                    <MenuAnchor
                      path={paths.teamsSettings}
                      label="Manage teams"
                      onClick={() => setShowOverlay(!showOverlay)}
                      color={page.primaryText}
                      className={style.accordionItem}
                    />
                  )}
                  {unitsSettingsEnabled && (
                    <MenuAnchor
                      path={paths.unitsSettings}
                      label={`Manage ${Case.lower(pluralize(unitTerm))}`}
                      onClick={() => setShowOverlay(!showOverlay)}
                      color={page.primaryText}
                      className={style.accordionItem}
                    />
                  )}
                  {showSettings && companySettings.map(({ api, display }) => (
                    <MenuAnchor
                      key={api}
                      path={paths.settings.replace(':id', api)}
                      label={display}
                      onClick={() => setShowOverlay(!showOverlay)}
                      color={page.primaryText}
                      className={style.accordionItem}
                    />
                  ))}
                </Box>
              </AccordionPanel>
            )}
            {cyclopsEnabled && (
              <AccordionPanel label={<MenuAnchor label="Cyclops" color={page.primaryText} />}>
                <Box align="stretch" pad={{ horizontal: '1rem' }}>
                  {!cyclopsAnalyticsDisabled && (
                    <MenuAnchor
                      path={paths.cyclopsAnalytics}
                      label="Analytics"
                      onClick={() => setShowOverlay(!showOverlay)}
                      color={page.primaryText}
                      className={style.accordionItem}
                    />
                  )}
                  {!isCyclopsDemo && (
                    <MenuAnchor
                      path={paths.cyclopsOrganizations}
                      label="Organizations"
                      onClick={() => setShowOverlay(!showOverlay)}
                      color={page.primaryText}
                      className={style.accordionItem}
                    />
                  )}
                  <MenuAnchor
                    path={paths.cyclopsEventsList.replace(':status', 'live')}
                    label="Live events"
                    onClick={() => setShowOverlay(!showOverlay)}
                    color={page.primaryText}
                    className={style.accordionItem}
                  />
                  <MenuAnchor
                    path={paths.cyclopsEventsList.replace(':status', 'past')}
                    label="Past events"
                    onClick={() => setShowOverlay(!showOverlay)}
                    color={page.primaryText}
                    className={style.accordionItem}
                  />
                  {!cyclopsSettingsDisabled && (
                    <MenuAnchor
                      path={paths.cyclopsSettings}
                      label="Settings"
                      onClick={() => setShowOverlay(!showOverlay)}
                      color={page.primaryText}
                      className={style.accordionItem}
                    />
                  )}
                </Box>
              </AccordionPanel>
            )}
          </StyledAccordion>
          <Box
            flex={false}
            direction="column"
            gap="1rem"
            pad={{ top: '2rem', left: '1.2rem' }}
            border={{
              side: 'top', color: navigation.border, style: 'solid', size: '1px',
            }}
          >
            <Box pad={{ left: '1rem' }}>
              <Text
                uppercase
                size="15px"
                weight={600}
                color={page.secondaryText}
                style={{ letterSpacing: '2.8px' }}
              >
                Quick Actions
              </Text>
            </Box>
            <Box>
              {!hideSearchFeatures && (
                <>
                  <MenuAnchor
                    path={paths.productNewSearch}
                    label="New search"
                    onClick={() => setShowOverlay(!showOverlay)}
                    color={page.primaryText}
                    icon={<FormNext size="1.5rem" color={page.altHighlight} />}
                  />
                  <MenuAnchor
                    path={paths.productResultsUpload}
                    label="Upload results"
                    onClick={() => setShowOverlay(!showOverlay)}
                    color={page.primaryText}
                    icon={<FormNext size="1.5rem" color={page.altHighlight} />}
                  />
                </>
              )}
              {useFeatures?.length > 0 && useFeatures.map(({ key, enabled }) => enabled && (
                <MenuAnchor
                  key={key}
                  path={selectFeatureRedirect(key)}
                  label={Case.capital(key)}
                  onClick={(key === 'export') ? (
                    () => { setShowOverlay(!showOverlay); }
                  ) : (
                    () => setShowOverlay(!showOverlay)
                  )}
                  color={page.primaryText}
                  icon={<FormNext size="1.5rem" color={page.altHighlight} />}
                />
              ))}
              <MenuAnchor
                path={paths.account}
                label="Account"
                onClick={() => setShowOverlay(!showOverlay)}
                color={page.primaryText}
                icon={<FormNext size="1.5rem" color={page.altHighlight} />}
              />
              <MenuAnchor
                path={paths.users}
                label="Manage users"
                onClick={() => setShowOverlay(!showOverlay)}
                color={page.primaryText}
                icon={<FormNext size="1.5rem" color={page.altHighlight} />}
              />
              <MenuAnchor
                label="Switch theme"
                onClick={() => {
                  // Optimistically update UI and backend
                  setDarkMode(!isDarkMode);
                  updateUserPreference('theme', !isDarkMode ? 'dark' : 'light');
                  setShowOverlay(!showOverlay);
                }}
                color={page.primaryText}
                icon={<FormNext size="1.5rem" color={page.altHighlight} />}
              />
            </Box>
          </Box>
          <Box flex direction="column" justify="end" pad={{ bottom: '1.5rem', horizontal: '1.85rem' }} gap="1.5rem">
            <ProfileSection
              small
              title={companyName}
              subtitle={`${firstName} ${lastName} - ${email}`}
              logoUrl={companyLogo}
              placeholder={accountPlaceholder}
              outlineColor={button.primaryBg}
              textColor={page.primaryText}
            />
            {displayStatusNotice && (
              <SystemStatusNotice
                message={statusMessage}
                textSize="1rem"
              />
            )}
          </Box>
        </Box>
      </NavbarSmallLayout>
    );
  }

  return (
    <NavbarAuthLayout
      navbarRef={authNavbarRef}
      isScroll={collapseOverride ? true : isScroll}
      navBg={navigation.background}
      darkMode={isDarkMode}
      borderColor={null}
    >
      <input
        hidden
        key={uniqid()}
        type="file"
        accept="image/*"
        onChange={(e) => (
          auth?.paymentPending ? null : uploadCompanyLogo(e.currentTarget.files[0])
        )}
        ref={companyLogoInputRef}
      />
      <Box direction="row" flex={{ grow: 2 }} justify="center" gap="0.5rem">
        {!hideSearchFeatures && (
          <Box direction="row" gap="0.5rem" alignSelf="start">
            <StyledAnchorButton
              path={paths.productNewSearch}
              label="New search"
              color={page.primaryText}
              buttonHoverColor={button.hoverBg}
              highlightText={button.hoverText}
              className="tour-new-search"
            />
            <StyledAnchorButton
              path={paths.productResultsUpload}
              label="Upload results"
              color={page.primaryText}
              buttonHoverColor={button.hoverBg}
              highlightText={button.hoverText}
              className="tour-upload-results"
            />
          </Box>
        )}
        {useFeatures?.length > 0 && (
          <Box
            flex={false}
            direction="row"
            gap="0.5rem"
            alignSelf="start"
          >
            {useFeatures.map(({ key, enabled }) => enabled && (
              <StyledAnchorButton
                key={key}
                path={selectFeatureRedirect(key)}
                label={Case.capital(key)}
                onClick={(key === 'export') ? () => null : () => null}
                color={page.primaryText}
                buttonHoverColor={button.hoverBg}
                highlightText={button.hoverText}
                className={`tour-${key}`}
              />
            ))}
          </Box>
        )}
        {contentIdEnabled && !isRedflagUpload && (
          <Box direction="row" gap="0.5rem" alignSelf="start">
            {contentIdAssetUploadEnabled && (
              <StyledAnchorButton
                path={paths.productAssetUpload}
                label="Upload assets"
                color={page.primaryText}
                buttonHoverColor={button.hoverBg}
                highlightText={button.hoverText}
              />
            )}
            <StyledAnchorButton
              path={paths.productManualClaimSubmit}
              pathMatch={(p) => p === paths.productManualClaimSubmit}
              label="Upload claims"
              color={page.primaryText}
              buttonHoverColor={button.hoverBg}
              highlightText={button.hoverText}
              className="tour-content-id-manual-claims-upload"
            />
          </Box>
        )}
      </Box>
      <Box direction="row" align="center" margin={{ right: '1rem' }} flex={{ grow: 0 }}>
        <ThemeToggleButton
          iconSize="1.4rem"
          setUserPreference={(key, value) => updateUserPreference(key, value)}
          showTooltip={showThemeTooltip}
        />
      </Box>
      <MenuDrop
        navBg={navigation.background}
        profileProps={{
          darkBackground: false,
          small: false,
          outlineColor: page.highlight,
          title: companyName,
          subTitle: `${firstName} ${lastName}`,
          logoUrl: companyLogo,
          placeholder: accountPlaceholder,
          textColor: page.primaryText,
          iconColor: button.iconColor,
        }}
      >
        {(closeHover) => (
          <Box pad="12px">
            <Box pad={{ vertical: '0.5rem', horizontal: '1rem' }} direction="column" gap="1rem">
              {displayStatusNotice && (
                <SystemStatusNotice
                  width={{ max: '14rem' }}
                  textSize="0.75rem"
                  message={statusMessage}
                />
              )}
              <Text size="0.85rem" color={page.altHighlight}>
                {email}
              </Text>
            </Box>
            <StyledProfileDropDownAnchor
              label="Account"
              onClick={closeHover}
              href={paths.account}
              primaryText={page.primaryText}
              hoverColor={button.hoverBg}
              highlightText={button.hoverText}
            />
            <StyledProfileDropDownAnchor
              label="Manage users"
              onClick={closeHover}
              href={paths.users}
              primaryText={page.primaryText}
              hoverColor={button.hoverBg}
              highlightText={button.hoverText}
            />
            <StyledProfileDropDownAnchor
              onClick={() => companyLogoInputRef.current.click()}
              label="Change company logo"
              primaryText={page.primaryText}
              hoverColor={button.hoverBg}
              highlightText={button.hoverText}
              disabled={auth?.paymentPending}
            />
            <StyledProfileDropDownAnchor
              onClick={() => startTour()}
              label="Tour your dashboard"
              primaryText={page.primaryText}
              hoverColor={button.hoverBg}
              highlightText={button.hoverText}
            />
            <StyledProfileDropDownAnchor
              hoverColor={button.hoverBg}
              primaryText={page.primaryText}
              highlightText={button.hoverText}
              label={(
                <Box direction="row" align="center" gap="0.5rem">
                  <Box flex={false} width="fit-content" height="fit-content" className="signOutIcon">
                    <StyledSVG
                      src={localAsset('images/sign-in-icon.svg')}
                      height="21px"
                      width="21px"
                      $fillColor={page.primaryText}
                    />
                  </Box>
                  Sign Out
                </Box>
              )}
              onClick={logOut}
            />
          </Box>
        )}
      </MenuDrop>
    </NavbarAuthLayout>
  );
};

function mapStateToProps(state) {
  const {
    isAuthenticated,
    email,
    firstName,
    lastName,
    companyId,
    companyName,
    companyLogo,
    companyFeatures,
    withAccount,
    paymentPending,
  } = state.auth;
  const {
    companySettings,
  } = state.companySettings;
  const { customerOnboardingData, customerOnboardingDataLoading } = state.payment;
  const { customReportsConfig } = state.appConfig;

  const auth = !isAuthenticated ? null : {
    email,
    firstName,
    lastName,
    companyId,
    companyName,
    companyLogo,
    companyFeatures,
    withAccount,
    paymentPending,
  };

  return {
    isAuthenticated,
    auth,
    companySettings,
    onboardingData: customerOnboardingData,
    onboardingDataLoading: customerOnboardingDataLoading,
    customReportsConfig,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    logOut: authLogOutRequest,
    uploadCompanyLogo: authUploadCompanyLogoRequest,
    fetchCompanySettings: initiateCompanySettingsRequest,
    startTour: startTourRequest,
    updateUserPreference: updateUserPreferenceRequest,
  }, dispatch);
}

AuthHeader.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  auth: PropTypes.shape({
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    companyId: PropTypes.number.isRequired,
    companyName: PropTypes.string.isRequired,
    companyLogo: PropTypes.string,
    companyFeatures: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      enabled: PropTypes.bool.isRequired,
      config: PropTypes.objectOf(PropTypes.any),
    })),
    withAccount: PropTypes.bool.isRequired,
    paymentPending: PropTypes.bool.isRequired,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  logOut: PropTypes.func.isRequired,
  uploadCompanyLogo: PropTypes.func.isRequired,
  updateUserPreference: PropTypes.func.isRequired,
  companyMaintenanceSettings: PropTypes.shape({
    showStatusMessage: PropTypes.bool,
    statusMessage: PropTypes.string,
  }),
  startTour: PropTypes.func.isRequired,
  small: PropTypes.bool.isRequired,
  fetchCompanySettings: PropTypes.func.isRequired,
  companySettings: PropTypes.arrayOf(PropTypes.shape({
    api: PropTypes.string.isRequired,
    display: PropTypes.string.isRequired,
  })),
  customReportsConfig: PropTypes.arrayOf(PropTypes.any),
  onboardingData: PropTypes.shape({
    onboardingId: PropTypes.string.isRequired,
    companyId: PropTypes.number.isRequired,
    paymentPending: PropTypes.bool.isRequired,
    paymentLink: PropTypes.string,
    youtubeCid: PropTypes.shape({
      uploader: PropTypes.string,
      plan: PropTypes.string,
    }),
  }),
  onboardingDataLoading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(AuthHeader);
